import {
  calcularUtilidadesPorQuincenaLicenciatario,
  groupDataByQuincena,
  groupDataByQuincenaByUpdate,
  groupDataByQuincenaOtros,
  sumUtilitiesByPeriodEmpaques
} from './groupDataByPeriod';
/**
 * Encuentra usuarios recursivamente en función del valor de hostUser.
 *
 * @param {Array} userList - La lista de usuarios.
 * @param {number} hostUserId - El ID del hostUser que se busca.
 * @param {Array} [result] - El resultado acumulado hasta el momento (opcional).
 * @returns {Array} - Un array que contiene los usuarios encontrados.
 */

export const findUsersByHostUser = (userList, hostUserId, result = []) => {
  // Check if the hostUserId exists in userList and add it to result if not already included
  const hostUser = userList.find((user) => user.id === hostUserId);
  if (hostUser && !result.find((resUser) => resUser.id === hostUserId)) {
    result.push(hostUser);
  }
  const usersWithHostUser = userList.filter((user) => user.hostUser === hostUserId);

  usersWithHostUser.forEach((user) => {
    if (!result.find((resUser) => resUser.id === user.id)) {
      result.push(user);
      findUsersByHostUser(userList, user.id, result);
    }
  });

  return result;
};

/**
 * Encuentra usuarios recursivamente en función del valor de hostUser.
 *
 * @param {Array} userList - La lista de usuarios.
 * @param {number} hostUserId - El ID del hostUser que se busca.
 * @param {Array} [result] - El resultado acumulado hasta el momento (opcional).
 * @returns {Array} - Un array que contiene los usuarios encontrados.
 */

export const findEnviosByHostUser = (listaEnvios, userId, result = []) => {
  // Check if the userId exists in listaEnvios and add it to result if not already included
  const envioDeUserId = listaEnvios.find((envio) => envio.user_id === userId);
  if (envioDeUserId && !result.find((resEnvio) => resEnvio.user_id === userId)) {
    result.push(envioDeUserId);
  }
  const enviosWithHostUser = listaEnvios.filter((envio) => envio.hostUser_id === userId);

  enviosWithHostUser.forEach((envio) => {
    if (!result.find((resEnvio) => resEnvio.user_id === envio.user_id)) {
      result.push(envio);
      findEnviosByHostUser(listaEnvios, envio.user_id, result);
    }
  });

  return result;
};

/**
 * Encuentra IDs de usuarios que existen en el objeto dado.
 *
 * @param {Array} userList - La lista de usuarios.
 * @param {Object} ventasPorUsuario - Un objeto donde las claves son IDs de usuario.
 * @returns {Array} - Un array que contiene los IDs de los usuarios encontrados.
 */
export const findUserIdsInVentas = (userList, ventasPorUsuario) => {
  const users = userList.filter((user) => Object.prototype.hasOwnProperty.call(ventasPorUsuario, user.id));
  const userIds = users.map((user) => user.id);
  return userIds;
};

/**
 * Encuentra usuarios recursivamente en función del valor de hostUser para cada usuario en la lista dada.
 *
 * @param {Array} userList - La lista de usuarios.
 * @param {Array} hostUserIds - Los IDs del hostUser que se buscan.
 * @returns {Object} - Un objeto donde cada clave es un ID de usuario y el valor es un array de usuarios relacionados.
 */
export const findUsersByHostUsers = (userList, hostUserIds) => {
  const result = {};

  hostUserIds.forEach((userId) => {
    const relatedUsers = findUsersByHostUser(userList, userId);
    result[userId] = relatedUsers.map((user) => user.id);
  });

  return result;
};

const utilidadAcordada = 6;

/**
 * Calcula las utilidades por quincena por licenciatario para diferentes secciones.
 *
 * @param {Object} usuariosPorLicenciatario - Un objeto que contiene usuarios agrupados por licenciatario.
 * @param {Object} ventasPorUsuario - Un objeto que contiene ventas agrupadas por usuario.
 * @param {String} seccion - Una cadena que define la sección a considerar. Puede ser 'envios', 'empaques', 'recargas'.
 * @returns {Object} Un objeto que contiene las utilidades por quincena por licenciatario.
 */
export const utilidadesPorQuincenaPorLicenciatario = (
  usuariosPorLicenciatario,
  ventasPorUsuario,
  seccion = 'envios',
  codigosDigitales
) => {
  const result = {};

  // Loop over each key in usuariosPorLicenciatario
  Object.keys(usuariosPorLicenciatario).forEach((userId) => {
    // For each userId, initialize an empty array for envios
    let ventas = [];

    // Loop over each relatedUserId
    usuariosPorLicenciatario[userId].forEach((relatedUserId) => {
      // If ventasPorUsuario has relatedUserId as a key, concatenate its value array to ventas
      if (Object.prototype.hasOwnProperty.call(ventasPorUsuario, relatedUserId)) {
        ventas = ventas.concat(ventasPorUsuario[relatedUserId]);

        // After concatenating, sort the array based on 'created_at' property
        ventas.sort((a, b) => b.created_at - a.created_at);
      }
    });

    // After the envios array is built and sorted for this userId, group it by quincena
    let ventasPorQuincena;
    if (seccion === 'envios') {
      ventasPorQuincena = groupDataByQuincena(ventas);
    } else if (seccion === 'empaques') {
      ventasPorQuincena = groupDataByQuincenaOtros(ventas);
    } else if (seccion === 'recargas') {
      ventasPorQuincena = groupDataByQuincenaByUpdate(ventas);
    } else {
      ventasPorQuincena = groupDataByQuincenaByUpdate(ventas);
    }

    // After the envios are grouped by quincena, calculate utilities
    let utilidadesPorQuincena;
    if (seccion === 'envios') {
      utilidadesPorQuincena = calcularUtilidadesPorQuincenaLicenciatario(ventasPorQuincena);
    } else if (seccion === 'empaques') {
      utilidadesPorQuincena = sumUtilitiesByPeriodEmpaques(ventasPorQuincena, true);
    } else if (seccion === 'recargas') {
      utilidadesPorQuincena = Object.values(ventasPorQuincena).map((quincenaData) => {
        const totalAmount = quincenaData.reduce((total, recarga) => {
          if (recarga.status === 'FAIL') {
            return total;
          }
          return total + recarga.totalAmount;
        }, 0);
        return totalAmount * 0.05 * 0.7; // 5% utilidad del servicio de recarga
      });
    } else {
      utilidadesPorQuincena = Object.values(ventasPorQuincena).map((quincenaData) =>
        quincenaData.reduce((acc, serv) => {
          if (serv.status === 'FAIL') {
            return acc;
          }
          // compare product id with the list of codigosdigitales
          const isCodigoDigital = codigosDigitales.some((codigoDigital) => serv.productId === codigoDigital);
          // eslint-disable-next-line no-nested-ternary
          const utilidad = serv.totalAmount
            ? isCodigoDigital
              ? serv.totalAmount * 0.03
              : serv.totalAmount - (serv.Amount ? Number(serv.Amount) : 0)
            : 0;

          // Only add utilidadAcordada if isCodigoDigital is false
          return isCodigoDigital ? acc + utilidad : acc + utilidad + utilidadAcordada;
        }, 0)
      );
    }

    // If seccion is not 'envios', convert object values into array and round off to two decimal places
    if (seccion !== 'envios') {
      utilidadesPorQuincena = Object.values(utilidadesPorQuincena).map((value) => Math.round(value * 100) / 100);
    }

    // Store utilidadesPorQuincena for this userId
    result[userId] = utilidadesPorQuincena;
  });

  return result;
};

/**
 * Encuentra IDs de usuarios que existen en el objeto dado.
 *
 * @param {Array} userList - La lista de usuarios.
 * @param {Object} enviosPorUsuario - Un objeto donde las claves son IDs de usuario.
 * @returns {Array} - Un array que contiene los IDs de los usuarios encontrados.
 */
export const findUserIdsInEnvios = (userList, enviosPorUsuario) => {
  const users = userList.filter((user) => Object.prototype.hasOwnProperty.call(enviosPorUsuario, user.id));
  const userIds = users.map((user) => user.id);
  return userIds;
};

export const unifyUtilidades = (
  userList,
  utilidadesVentaEmPorLicen,
  utilidadesRecargasPorLicen,
  utilidadesServiciosPorLicen,
  utilidadEnviosPorLicen
) => {
  // create a mapping from userIds to displayNames
  const userIdToDisplayName = {};
  userList.forEach((user) => {
    userIdToDisplayName[user.id] = user.displayName;
  });

  // get all the keys from all the input objects
  const allKeys = [
    ...new Set([
      ...Object.keys(utilidadesVentaEmPorLicen),
      ...Object.keys(utilidadesRecargasPorLicen),
      ...Object.keys(utilidadesServiciosPorLicen),
      ...Object.keys(utilidadEnviosPorLicen)
    ])
  ];

  // Create an object for the result
  const result = {};

  // iterate through each key
  allKeys.forEach((key) => {
    // get the displayName for this key
    const displayName = userIdToDisplayName[key] || key;
    result[displayName] = {
      utilidadesVentaEmPorLicen: utilidadesVentaEmPorLicen[key] || [],
      utilidadesRecargasPorLicen: utilidadesRecargasPorLicen[key] || [],
      utilidadesServiciosPorLicen: utilidadesServiciosPorLicen[key] || [],
      utilidadEnviosPorLicen: utilidadEnviosPorLicen[key] || []
    };
  });

  return result;
};
